

















































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import Category from '@/models/Category';
import { CategoryMagazineCount, MagazineFilterValues } from '@/store/storeStateInterfaces';

const MagazineStore = namespace('magazine');
const ApplicationStore = namespace('application');

@Component({
    components: {
        AccessibilityButtonComponent: () => import(
            /* webpackChunkName: "AccessibilityButtonComponent" */
            '@/components/shared/AccessibilityButton.component.vue')
    }
})
export default class CategoryMenuComponent extends Vue {

    @MagazineStore.Getter('categories')
    public categories!: Category[];

    @MagazineStore.Action('getCategories')
    public getCategories!: () => Category[];

    @MagazineStore.Action('applyFilterCategory')
    public applyFilterCategory!: (category?: Category) => void;

    @MagazineStore.Getter('categoryMagazineCount')
    public categoryMagazineCount?: CategoryMagazineCount;

    @MagazineStore.Getter('filterValues')
    public _filterValues!: MagazineFilterValues;

    @ApplicationStore.Getter('accessibilityControls')
    private _accessibilityControls!: boolean;

    @ApplicationStore.Getter('noAnimations')
    private noAnimations!: boolean;

    @ApplicationStore.Getter('categoryMenu')
    public _categoryMenu!: boolean;

    @ApplicationStore.Mutation('setCategoryMenu')
    private setCategoryMenu!: (value: boolean) => void;

    public areImagesLoaded: boolean = false;
    public imageLoadCounter: number = 0;

    get categoryMenu(): boolean {
        return this._categoryMenu ?? false;
    }

    set categoryMenu(value: boolean) {
        this.setCategoryMenu(value);
    }

    /**
     * Scrolls to the active category if categoryMenu opens and the images are loaded
     */
    @Watch('categoryMenu')
    private onCategoryMenuChange() {
        if (this.categoryMenu && this.filterValues.category?.id && this.areImagesLoaded) {
            Vue.nextTick(() => {
                const selectedCategoryView = document.getElementById(this.filterValues.category?.id!)!;
                if (selectedCategoryView) {
                    selectedCategoryView.scrollIntoView();
                }
            });
        }
    }

    get accessibilityControls(): boolean {
        return this._accessibilityControls;
    }

    get filterValues(): MagazineFilterValues {
        return this._filterValues;
    }

    get scrollContainer(): HTMLElement {
        return this.$refs['scroll-container'] as HTMLDivElement;
    }

    /**
     * Function to get categories and set selected category after locale changed
     */
    private async getCategoriesByLocaleChange(hasOldLocale: boolean) {
        await this.getCategories();

        if (hasOldLocale) {
            const filterCategories = this.categories.filter(category => category.id === this._filterValues.category?.id);

            if (filterCategories.length === 1) {
                this._filterValues.category = filterCategories[0];
            }
        }
    }

    public async mounted() {
        // do no use loading mechanism while in noAnimations modus
        if (this.noAnimations) {
            this.areImagesLoaded = true;
        }
        this.$watch(
            '$i18n.locale',
            (newLocale, oldLocale) => {
                if (newLocale !== oldLocale) {
                    const hasOldLocale = !!oldLocale;

                    this.getCategoriesByLocaleChange(hasOldLocale);
                }
            },
            { immediate: true }
        );
    }

    /**
     *  Support function to determine when all images were loaded
     */
    public imageLoadDone() {
        this.imageLoadCounter++;
        if (this.imageLoadCounter === this.categories.length + 1) {
            this.areImagesLoaded = true;
            // trigger focusing correct category
            this.onCategoryMenuChange();
        }
    }

    /**
     *  Support function to resolve the necessary classes
     */
    get categoryInformationClasses(): string {
        const classes = [];
        if (this.$vuetify.breakpoint.width <= 800) {
            classes.push('full-width');
        }
        if (this.$vuetify.breakpoint.width <= 500) {
            classes.push('small-font');
        }
        if (this.$vuetify.breakpoint.width <= 390) {
            classes.push('smaller-font');
        }
        return classes.join(' ');
    }

    /**
     * Event handler for category selection.
     * @param category
     */
    public onCategoryClick(category?: Category) {
        this.applyFilterCategory(category);
        this.setCategoryMenu(false);
    }

    /**
     * Event handler for category menu cancel.
     * @param category
     */
    public onCategoryMenuCancel() {
        this.setCategoryMenu(false);
    }

    /**
     * Support function to return available magazines for category while considering language
     */
    public getCategoryMagazineCount(category: Category): string {
        if (this.categoryMagazineCount?.all) {
            let count = 0;
            if (this.filterValues.languages.length > 0) {
                this.filterValues.languages.forEach(language => {
                    count += this.categoryMagazineCount![language][category?.id ?? 'total'] ?? 0;
                });
            } else {
                count = this.categoryMagazineCount.all[category?.id ?? 'total'] ?? 0;
            }
            return `${count}`;
        } else {
            return '0';
        }
    }

    public getCategoryImage(category: Category): string {
        let imported;
        try {
            if (category) {
                imported = require('../../assets/images/categories/' + category.id + '.jpg');
            } else {
                imported = require('../../assets/images/categories/all.jpg');
            }
        } catch (e) {
            // use all as default image
            imported = require('../../assets/images/categories/all.jpg');
        } finally {
            // eslint-disable-next-line no-unsafe-finally
            return imported;
        }
    }

    /**
     * Function to scroll down!
     * TODO maybe it make sense to provide scroll class for all scrolling activites. For example
     * these scroll functions are used in library as well
     */
    public scrollBottom() {
        this.scrollContainer.scrollBy({
            top: 100,
            left: 0,
            behavior: 'smooth'
        });
    }

    /**
     * Function to scroll up!
     */
    public scrollTop() {
        this.scrollContainer.scrollBy({
            top: -100,
            left: 0,
            behavior: 'smooth'
        });
    }
}
